import styled, { css } from 'styled-components'
import { IconComponent, themeColor, themeFontFamily, UISizes, UITypes } from 'LibNeo'
import { Link } from 'react-router-dom'
import { DropzoneStyled } from 'LibNeo/components/Basic/Upload/styled'

export type IconProps = {
	withIndicator?: string
	left?: string | JSX.Element
	right?: string | JSX.Element
	icons?: string
}

const colorsByType = {
	[UITypes.DANGER]: themeColor('pink', 5),
	[UITypes.PRIMARY]: themeColor('emerald', 5),
	[UITypes.WARNING]: themeColor('gold', 5),
	[UITypes.INFO]: themeColor('blue', 5),
	[UITypes.PASSIVE]: themeColor('gray', 1),
}

const activeColorsByType = {
	[UITypes.DANGER]: themeColor('pink', 6),
	[UITypes.PRIMARY]: themeColor('emerald', 6),
	[UITypes.WARNING]: themeColor('gold', 6),
	[UITypes.INFO]: themeColor('blue', 6),
	[UITypes.PASSIVE]: themeColor('gray', 3),
}

const disabledColorsByType = {
	[UITypes.DANGER]: themeColor('pink', 3),
	[UITypes.PRIMARY]: themeColor('emerald', 3),
	[UITypes.WARNING]: themeColor('gold', 3),
	[UITypes.INFO]: themeColor('blue', 3),
	[UITypes.PASSIVE]: themeColor('gray', 2),
}

const Btn = css<{
	link?: boolean
	outline?: boolean
	disabled?: boolean
	block?: boolean
	types?: UITypes
	size?: UISizes
	right?: boolean
	left?: boolean
	icons?: boolean
	iconOnly?: boolean
	withIndicator?: boolean
}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	outline: none;
	border-radius: 6px;
	border: 1px solid transparent;
	text-align: center;
	font-family: ${themeFontFamily('SBSansInterface')};
	font-style: normal;
	font-weight: 400;
	text-decoration: none;
	vertical-align: middle;
	cursor: pointer;
	font-size: 14px;
	line-height: 16px;

	&:hover {
		transition: 0.3s;
		svg {
			transition: 0.3s fill;
		}
	}

	${({ block }) =>
		block &&
		css`
			width: 100%;
			margin: 0;

			&:not(:nth-of-type(1)) {
				margin: 20px 0 0 0;
			}
		`}

	${({ size, right, left, icons, iconOnly }) => {
		if (size) {
			if (left)
				return css`
					padding: ${size === UISizes.SMALL ? '5px 7px 5px 5px' : '11px 15px 11px 11px'};
				`

			if (right)
				return css`
					padding: ${size === UISizes.SMALL ? '5px 5px 5px 7px' : '11px 11px 11px 15px'};
				`

			if (icons)
				return css`
					padding: ${size === UISizes.SMALL ? '5px' : '11px'};
				`

			if (iconOnly)
				return css`
					padding: ${size === UISizes.SMALL ? '5px' : '11px'};
				`

			return css`
				padding: ${size === UISizes.SMALL ? '5px 7px' : '11px 15px'};
			`
		}
	}}

	${({ disabled }) =>
		disabled &&
		css`
			cursor: not-allowed;
		`}

	${({ types, disabled }) => {
		if (disabled && types)
			return css`
				background-color: ${disabledColorsByType[types]};
				border: 1px solid transparent;
				color: ${types === UITypes.PASSIVE ? themeColor('gray', 4) : themeColor('white')};
				svg {
					fill: ${types === UITypes.PASSIVE ? themeColor('gray', 4) : themeColor('white')};
				}

				&:focus {
					box-shadow: none;
				}
			`

		return css`
			& {
				background-color: ${colorsByType[types]};
				color: ${types === UITypes.PASSIVE ? themeColor('gray', 6) : themeColor('white')};

				svg {
					fill: ${types === UITypes.PASSIVE ? themeColor('gray', 6) : themeColor('white')};
				}
			}

			&:hover,
			&:active {
				background-color: ${activeColorsByType[types]};
				color: ${types === UITypes.PASSIVE ? themeColor('gray', 8) : themeColor('white')};

				svg {
					fill: ${types === UITypes.PASSIVE ? themeColor('gray', 8) : themeColor('white')};
				}
			}

			&:focus {
				box-shadow: 0 0 0 3px ${activeColorsByType[types]};
				background-color: ${activeColorsByType[types]};
				color: ${types === UITypes.PASSIVE ? themeColor('gray', 8) : themeColor('white')};
				svg {
					fill: ${types === UITypes.PASSIVE ? themeColor('gray', 8) : themeColor('white')};
				}
			}
		`
	}}

	${({ link, disabled }) => {
		if (link) {
			if (!disabled)
				return css`
					&,
					&:visited {
						background-color: transparent;
						font-size: 14px;
						color: ${themeColor('emerald', 5)};
						text-decoration: none;

						svg {
							fill: ${themeColor('emerald', 5)};
						}
					}
					&:hover,
					&:active {
						background-color: transparent;
						color: ${themeColor('emerald', 6)};
						outline: none;

						svg {
							fill: ${themeColor('emerald', 6)};
						}
					}

					&:focus {
						box-shadow: 0 0 0 3px ${themeColor('emerald', 6)};
						background-color: transparent;
						color: ${themeColor('emerald', 6)};
						outline: none;

						svg {
							fill: ${themeColor('emerald', 6)};
						}
					}
				`
			return css`
				background-color: transparent;
				color: ${themeColor('emerald', 3)};
				border: 1px solid transparent;

				&:hover {
					color: ${themeColor('emerald', 3)};
				}

				&:focus {
					box-shadow: none;
					border-color: transparent;
				}

				svg {
					fill: ${themeColor('emerald', 3)};
				}
			`
		}
	}}


	${({ outline, disabled }) => {
		if (outline) {
			if (!disabled)
				return css`
					& {
						border: 1px solid ${themeColor('gray', 3)};
						background-color: ${themeColor('white')};
						color: ${themeColor('gray', 8)};

						svg {
							fill: ${themeColor('gray', 8)};
						}
					}
					&:hover,
					&:active {
						background-color: ${themeColor('white')};
						border: 1px solid ${themeColor('gray', 5)};
						color: ${themeColor('gray', 8)};
						svg {
							fill: ${themeColor('gray', 8)};
						}
					}
					&:focus {
						background-color: ${themeColor('white')};
						box-shadow: 0 0 0 3px ${themeColor('gray', 4)};
						border-color: transparent;
						color: ${themeColor('gray', 8)};

						svg {
							fill: ${themeColor('gray', 8)};
						}
					}
				`
			return css`
				border: 1px solid ${themeColor('gray', 3)};
				background-color: ${themeColor('white')};
				color: ${themeColor('gray', 4)};

				svg {
					fill: ${themeColor('gray', 4)};
				}
			`
		}
	}}

	& + ${DropzoneStyled} {
		margin-left: 20px;
	}
`

export const HrefBtn = styled.a`
	${Btn}
`

export const LinkBtn = styled(Link)`
	${Btn}
`

export const Button = styled.button`
	${Btn}
`

export const WrapperIcon = styled(IconComponent)<IconProps>`
	margin: 0;
	padding: 0;
	overflow: unset;
	${({ withIndicator = false }) =>
		withIndicator &&
		css`
			&:before {
				content: '';
				display: block;
				position: absolute;
				z-index: 4;
				top: -2px;
				right: -2px;
				width: 10px;
				height: 10px;
				background-color: ${themeColor('crimson')};
				border: 2px solid ${themeColor('colorMainBg')};
				border-radius: 50%;
			}
		`}
	${({ left = false }) =>
		left &&
		css`
			margin: 0 6px 0 0;
		`}
	${({ right = false }) =>
		right &&
		css`
			margin: 0 0 0 6px;
		`}
	${({ icons = false }) =>
		icons &&
		css`
			&:first-child {
				margin: 0 6px 0 0;
			}
			&:last-child {
				margin: 0 0 0 6px;
			}
		`}
`

export const BlockButtons = styled.div<{ gap?: number }>`
	display: flex;
	gap: ${({ gap = 10 }) => `${gap}px`};
`
